import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RateLimitError } from '@atsready/shared/utils';
import type { CampaignOperationDetail, CampaignOperationPreview, CampaignOperationPreviewInput } from '@atsready/shared';
import * as api from '@/services/campaign-email-operations';

class OperationDeadlineError extends Error {}
async function bounded<T>(request: (signal: AbortSignal) => Promise<T>): Promise<T> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout>;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => { controller.abort(); reject(new OperationDeadlineError()); }, 45_000);
  });
  try { return await Promise.race([request(controller.signal), deadline]); }
  finally { clearTimeout(timer!); }
}

/** No automatic dispatch: a confirmed action or deliberate Resume owns each sequential run. */
export function useCampaignEmailOperation() {
  const client = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [recipientCursor, setRecipientCursor] = useState<string>();
  const [historyCursor, setHistoryCursor] = useState<string>();
  const historyTrail = useRef<Array<string | undefined>>([]);
  const recipientTrail = useRef<Array<string | undefined>>([]);
  const [preview, setPreview] = useState<CampaignOperationPreview | null>(null);
  const [previewCursor, setPreviewCursor] = useState<string>();
  const previewTrail = useRef<Array<string | undefined>>([]);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<'error' | 'uncertain' | 'rate' | null>(null);
  const [blockedUntil, setBlockedUntil] = useState(0);
  const mounted = useRef(true);
  const generation = useRef(0);
  const draftGeneration = useRef(0);
  const active = useRef(false);
  const cooldown = useRef(0);
  const key = useRef({ operationId: '', idempotencyKey: '' });
  const detailKey = ['campaign-email-detail', selectedId, recipientCursor];
  const latest = useRef<CampaignOperationDetail | undefined>(undefined);

  const stopGenerations = useCallback(() => { generation.current++; draftGeneration.current++; }, []);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; stopGenerations(); };
  }, [stopGenerations]);
  useEffect(() => {
    if (!blockedUntil) return;
    const timer = setTimeout(() => { setBlockedUntil(0); }, Math.max(0, blockedUntil - Date.now()));
    return () => clearTimeout(timer);
  }, [blockedUntil]);

  const report = useCallback((cause: unknown) => {
    if (!mounted.current) return;
    if (cause instanceof RateLimitError) {
      cooldown.current = Date.now() + Math.max(1, cause.retryAfter ?? 60) * 1000;
      setBlockedUntil(cooldown.current);
      setError('rate');
    } else setError(cause instanceof OperationDeadlineError ? 'uncertain' : 'error');
  }, []);
  const readOptions = { staleTime: 30_000, retry: false, refetchOnWindowFocus: false, refetchOnReconnect: false, meta: { skipGlobalErrorHandler: true } } as const;
  const history = useQuery({
    queryKey: ['campaign-email-history', historyCursor],
    queryFn: async ({ signal }) => {
      try { return await api.listCampaignEmailOperations(historyCursor ? { cursor: historyCursor } : {}, signal); }
      catch (cause) { if (!signal.aborted) report(cause); throw cause; }
    },
    ...readOptions,
  });
  const status = useQuery({
    queryKey: detailKey,
    enabled: selectedId !== null,
    queryFn: async ({ signal }) => {
      try { return await api.getCampaignEmailOperation(selectedId!, recipientCursor, signal); }
      catch (cause) { if (!signal.aborted) report(cause); throw cause; }
    },
    ...readOptions,
  });
  const previewPage = useQuery({
    queryKey: ['campaign-email-preview-page', preview?.operation.id, previewCursor],
    enabled: preview !== null && previewCursor !== undefined,
    queryFn: async ({ signal }) => {
      try { return await api.getCampaignEmailOperation(preview!.operation.id, previewCursor, signal); }
      catch (cause) { if (!signal.aborted) report(cause); throw cause; }
    },
    ...readOptions,
  });
  useEffect(() => { latest.current = status.data; }, [status.data]);
  const mutation = useMutation({ mutationFn: (action: () => Promise<void>) => action(), retry: false, onError: report });
  const store = (detail: CampaignOperationDetail) => {
    latest.current = detail;
    client.setQueryData(['campaign-email-detail', detail.operation.id, undefined], detail);
    client.setQueryData(['campaign-email-history', historyCursor], (old: typeof history.data) => old && ({ ...old, operations: old.operations.map(operation => operation.id === detail.operation.id ? detail.operation : operation) }));
    recipientTrail.current = [];
    setRecipientCursor(undefined);
    setSelectedId(detail.operation.id);
  };
  const allowed = () => mounted.current && !active.current && Date.now() >= cooldown.current;
  const refreshStatus = async (id: string) => {
    try { const detail = await api.getCampaignEmailOperation(id); if (mounted.current) store(detail); }
    catch (cause) { report(cause); }
  };
  const execute = async (action: (run: number) => Promise<void>, recoveryOperationId?: string) => {
    if (!allowed()) return;
    active.current = true;
    const run = ++generation.current;
    setError(null);
    try { await mutation.mutateAsync(() => action(run)); }
    catch (cause) {
      if (!(cause instanceof RateLimitError) && recoveryOperationId && mounted.current) await refreshStatus(recoveryOperationId);
    }
    finally { active.current = false; if (mounted.current) setRunning(false); }
  };
  const process = async (id: string, run: number) => {
    setRunning(true);
    while (mounted.current && run === generation.current) {
        const result = await bounded(signal => api.processCampaignEmailOperation(id, signal));
        if (!mounted.current) return;
        store(result);
        if (run !== generation.current || !result.hasMore || result.paused || result.operation.counts.unknown > 0 || (!result.operation.providerBlockedUntil && result.operation.providerBlockReason) || (result.operation.providerBlockedUntil && Date.parse(result.operation.providerBlockedUntil) > Date.now())) return;
    }
  };
  const pause = () => { generation.current++; setRunning(false); };
  const selectOperation = (id: string) => {
    pause(); recipientTrail.current = []; setSelectedId(id); setRecipientCursor(undefined); setError(null);
  };
  const invalidatePreview = useCallback(() => { draftGeneration.current++; previewTrail.current = []; setPreviewCursor(undefined); setPreview(null); }, []);
  const prepare = async (campaignId: string, input: CampaignOperationPreviewInput) => {
    const revision = draftGeneration.current;
    await execute(async () => {
      const review = await bounded(signal => api.previewCampaignEmail(campaignId, input, signal));
      if (mounted.current && revision === draftGeneration.current) {
        key.current = { operationId: review.operation.id, idempotencyKey: crypto.randomUUID() };
        previewTrail.current = []; setPreviewCursor(undefined);
        setPreview(review);
      }
    });
  };
  const confirm = async () => {
    if (!preview || preview.operation.counts.eligible === 0 || Date.parse(preview.operation.reviewExpiresAt) <= Date.now()) return;
    const reviewed = preview;
    const revision = draftGeneration.current;
    await execute(async run => {
      const detail = await bounded(signal => api.confirmCampaignEmailOperation(reviewed.operation.id, { idempotencyKey: key.current.idempotencyKey }, signal));
      if (!mounted.current) return;
      store(detail); setPreview(null);
      if (run !== generation.current || revision !== draftGeneration.current) return;
      await process(detail.operation.id, run);
    }, reviewed.operation.id);
  };
  const canResume = () => {
    const operation = latest.current?.operation;
    return operation?.reviewState === 'confirmed' && operation.counts.pending + operation.counts.preparing + operation.counts.sending > 0 && operation.counts.unknown === 0 && !(!operation.providerBlockedUntil && operation.providerBlockReason) && (!operation.providerBlockedUntil || Date.parse(operation.providerBlockedUntil) <= Date.now());
  };
  const resume = async () => {
    if (!selectedId || !canResume()) return;
    await execute(run => process(selectedId, run), selectedId);
  };
  const retry = async () => {
    if (!selectedId) return;
    await execute(async () => {
      const detail = await bounded(signal => api.retryCampaignEmailOperation(selectedId, signal));
      if (mounted.current) store(detail);
    }, selectedId);
  };
  const reconcile = async (recipientId: string) => {
    if (!selectedId) return;
    await execute(async () => {
      const detail = await bounded(signal => api.reconcileCampaignEmailOperation(selectedId, recipientId, signal));
      if (mounted.current) store(detail);
    }, selectedId);
  };
  const reload = async () => { if (selectedId && allowed()) await refreshStatus(selectedId); };
  const reloadHistory = async () => { if (allowed()) await history.refetch(); };
  const nextHistory = () => { if (history.data?.nextCursor && allowed()) { historyTrail.current.push(historyCursor); setHistoryCursor(history.data.nextCursor); } };
  const previousHistory = () => { if (allowed()) setHistoryCursor(historyTrail.current.pop()); };
  const nextRecipients = () => { if (status.data?.nextCursor && allowed()) { recipientTrail.current.push(recipientCursor); setRecipientCursor(status.data.nextCursor); } };
  const previousRecipients = () => { if (allowed()) setRecipientCursor(recipientTrail.current.pop()); };
  const previewRecipients = previewPage.data?.recipients ?? preview?.recipients;
  const previewNextCursor = previewCursor === undefined ? preview?.nextCursor : previewPage.data?.nextCursor;
  const nextPreviewRecipients = () => {
    if (previewNextCursor && !previewPage.isFetching && allowed()) { previewTrail.current.push(previewCursor); setPreviewCursor(previewNextCursor); }
  };
  const previousPreviewRecipients = () => {
    if (!previewPage.isFetching && allowed()) setPreviewCursor(previewTrail.current.pop());
  };
  const reloadPreviewRecipients = async () => {
    if (previewCursor !== undefined && allowed()) await previewPage.refetch();
  };
  return { history, detail: status.data, statusLoading: status.isFetching, preview, running, busy: mutation.isPending, error, blockedUntil,
    historyCursor, recipientCursor, previewCursor, previewRecipients, previewNextCursor, previewLoading: previewPage.isFetching, previewPageError: previewPage.isError, nextPreviewRecipients, previousPreviewRecipients, reloadPreviewRecipients, prepare, invalidatePreview, confirm, selectOperation, resume, pause, retry, reconcile, reload, reloadHistory, nextHistory, previousHistory, nextRecipients, previousRecipients };
}
